import React, { useState } from 'react';
import Map from './components/Map';
import AuthView from './components/AuthView';
import BusinessesView from './components/BusinessesView';
import NotificationsView from './components/NotificationsView';
import ProfileView from './components/ProfileView';
import { useAuth } from './context/AuthContext';
import logo from './assets/logo.png';
import './App.css';

type Tab = 'map' | 'businesses' | 'notifications' | 'profile';

const App: React.FC = () => {
  const { isAuthenticated, isGuest, isLoading, user, hasValidAccess, exitGuestMode } = useAuth();
  const [tab, setTab] = useState<Tab>('map');
  const showAllPins = false;
  const [mapType, setMapType] = useState<'standard' | 'satellite'>('standard');
  const [userLocation, setUserLocation] = useState<GeolocationCoordinates | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#f5f7fa'
      }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
          Loading…
        </div>
      </div>
    );
  }

  // Guests can browse the map/businesses read-only; everything else
  // stays behind sign-in (matches the write rules in firestore.rules)
  if (!isAuthenticated && !isGuest) {
    return <AuthView />;
  }

  const handleLocationClick = () => {
    if (userLocation) {
      window.dispatchEvent(new CustomEvent('centerOnLocation', {
        detail: { lat: userLocation.latitude, lng: userLocation.longitude }
      }));
    } else {
      setIsLoadingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation(position.coords);
          window.dispatchEvent(new CustomEvent('centerOnLocation', {
            detail: { lat: position.coords.latitude, lng: position.coords.longitude }
          }));
          setIsLoadingLocation(false);
        },
        () => {
          setLocationError('Unable to get your location. Please check your browser settings.');
          setIsLoadingLocation(false);
        }
      );
    }
  };

  const tabs: { id: Tab; label: string; emoji: string }[] = [
    { id: 'map', label: 'Map', emoji: '🗺️' },
    { id: 'businesses', label: 'Businesses', emoji: '🏪' },
    { id: 'notifications', label: 'Alerts', emoji: '🔔' },
    { id: 'profile', label: 'Profile', emoji: '👤' }
  ];

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="brand-section">
            <img src={logo} alt="2EZ" className="brand-logo" />
            <p className="brand-subtitle">
              Community-driven map for St. Kitts & Nevis
              {user && ` • Hi, ${user.displayName}`}
              {isGuest && ' • Guest'}
            </p>
          </div>

          <div className="controls-section">
            <button
              className="location-button"
              onClick={handleLocationClick}
              title={userLocation ? 'Center on your location' : 'Get your location'}
            >
              {isLoadingLocation ? '⏳' : '📍'}
            </button>

            <button
              className="map-mode-button-single"
              onClick={() => setMapType(mapType === 'standard' ? 'satellite' : 'standard')}
              title={`Switch to ${mapType === 'standard' ? 'Aerial' : 'Streets'} view`}
            >
              🌍
            </button>
          </div>
        </div>
      </header>

      {isAuthenticated && !hasValidAccess && (
        <div style={{
          backgroundColor: '#ff9500', color: 'white', padding: '8px 16px',
          fontSize: '13px', textAlign: 'center', fontWeight: 600
        }}>
          Your free trial has expired — request a subscription from your Profile tab.
        </div>
      )}
      {locationError && (
        <div style={{
          backgroundColor: '#ff3b30', color: 'white', padding: '8px 16px',
          fontSize: '13px', textAlign: 'center'
        }}>
          {locationError}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {tab === 'map' && <Map showAllPins={showAllPins} mapType={mapType} />}
        {tab === 'businesses' && <BusinessesView />}
        {(tab === 'notifications' || tab === 'profile') && (
          isAuthenticated ? (
            tab === 'notifications' ? <NotificationsView /> : <ProfileView />
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '24px', backgroundColor: '#f5f7fa'
            }}>
              <div style={{
                backgroundColor: 'white', borderRadius: '16px', padding: '32px',
                maxWidth: '320px', textAlign: 'center',
                boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
              }}>
                <p style={{ margin: '0 0 16px', color: '#333', fontSize: '15px' }}>
                  Sign in to view {tab === 'notifications' ? 'alerts' : 'your profile'},
                  create pins, and join chats.
                </p>
                <button
                  onClick={exitGuestMode}
                  style={{
                    width: '100%', padding: '12px', backgroundColor: '#007aff',
                    color: 'white', border: 'none', borderRadius: '10px',
                    fontSize: '15px', fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  Sign In
                </button>
              </div>
            </div>
          )
        )}
      </div>

      {/* Bottom tab bar — mirrors iOS MainTabView */}
      <nav style={{
        display: 'flex', borderTop: '1px solid #e0e0e0', backgroundColor: 'white',
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, padding: '10px 4px 8px', border: 'none', cursor: 'pointer',
              backgroundColor: 'transparent',
              color: tab === t.id ? '#007aff' : '#888',
              fontSize: '11px', fontWeight: tab === t.id ? 700 : 500,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'
            }}
          >
            <span style={{ fontSize: '20px' }}>{t.emoji}</span>
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

export default App;
